import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, X } from "lucide-react";
import { MagneticButton } from "./MagneticButton";
import { Logo } from "./Logo";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const LINKS = [
  { id: "about", label: "About" },
  { id: "projects", label: "Projects" },
  { id: "experience", label: "Experience" },
  { id: "testimonials", label: "Voices" },
  { id: "blog", label: "Writing" },
  { id: "contact", label: "Contact" }
];

export const Nav = () => {
  const [active, setActive] = useState("hero");
  const [open, setOpen] = useState(false);
  const navRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.3 });

  useEffect(() => {
    const sections = ["hero", ...LINKS.map((l) => l.id)];
    const observers = [];
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const onLink = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header ref={navRef} className="sticky top-0 z-50 backdrop-blur-md bg-ink-950/55 border-b border-edge-650">
      <div className="container-page h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="group flex items-center gap-3 outline-none"
          aria-label="Malicke Case home"
        >
          <Logo compact className="h-9 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => onLink(l.id)}
              className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                active === l.id ? "text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {l.label}
              {active === l.id && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute left-3 right-3 -bottom-[1px] h-[2px] bg-magenta-500 rounded-full"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="/resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-resume-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-edge-700 px-3 py-2 text-sm font-medium text-zinc-100 hover:border-magenta-500/60 hover:bg-graphite-900/50 transition-colors"
          >
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-magenta-300">PDF</span>
            Resume
          </a>
          <span data-magnetic-wrap="true" className="hidden sm:inline-flex">
            <MagneticButton
              onClick={() => onLink("contact")}
              data-testid="nav-contact-cta"
              className="rounded-xl bg-magenta-500 text-ink-950 px-4 py-2 text-sm font-semibold hover:bg-magenta-300 glow-magenta"
            >
              Get in touch
            </MagneticButton>
          </span>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-edge-700 text-zinc-200"
                aria-label="Open menu"
                data-testid="nav-mobile-toggle"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink-900 border-edge-700 text-zinc-100 w-[84%] sm:max-w-sm p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-5 h-16 border-b border-edge-700">
                  <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-zinc-400">Menu</span>
                  <button className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-edge-700" onClick={() => setOpen(false)} aria-label="Close menu">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto py-4">
                  {LINKS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`nav-mobile-${l.id}-link`}
                      onClick={() => onLink(l.id)}
                      className={`w-full text-left px-5 py-3 text-lg font-display tracking-tight ${
                        active === l.id ? "text-white" : "text-zinc-300"
                      } hover:bg-graphite-900`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
                <div className="p-5 border-t border-edge-700 flex flex-col gap-3">
                  <a href="/resume.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex justify-center rounded-xl border border-edge-700 px-4 py-2 text-sm">Download Resume</a>
                  <button onClick={() => onLink("contact")} className="inline-flex justify-center rounded-xl bg-magenta-500 text-ink-950 px-4 py-2 text-sm font-semibold">Get in touch</button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <motion.div style={{ scaleX: progress }} className="h-[2px] bg-magenta-500 origin-left" />
    </header>
  );
};
